<script setup lang="ts">

import { usePlanetStore } from '@/stores/planetStore.ts'
import { computed, defineProps, ref, onMounted, watch } from 'vue'
import Loader from '@/components/Elements/Loaders/Loader.vue'
import Backend from '@/models/backend.ts'
import ContentItem from '@/components/Sections/Planets/PlanetTabs/StatsTab/ContentItem.vue'
import { useAssetStore } from '@/stores/assetStore.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import { logger } from '@/models/logger';
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Props
 */
const props = defineProps({
  planetId: {
    type: Number,
    required: true
  }
});

/**
 * Stores / models
 */
const planetStore = usePlanetStore()
const assetStore = useAssetStore()
const gameStore = useGameStore()

/**
 * Variables
 */
const loading = ref(true)
const stats = ref<Record<string, any>>({}); // Use a specific type instead of `Record<string, any>` if possible

/**
 * Computed
 */
const planetData = computed(() => planetStore.findPlanetById(props.planetId)!)

const title = computed(() => {
  if(planetStore.planetStatsTab === 'food') {
    return t('planets.stats.titles.foodYield')
  } else if (planetStore.planetStatsTab === 'production') {
    return t('planets.stats.titles.production')
  } else if (planetStore.planetStatsTab === 'science') {
    return t('planets.stats.titles.science')
  } else if (planetStore.planetStatsTab === 'credit') {
    return t('planets.stats.titles.credits')
  } else if (planetStore.planetStatsTab === 'energy') {
    return t('planets.stats.titles.energy')
  } else if (planetStore.planetStatsTab === 'energy_storage') {
    return t('planets.stats.titles.energyStorage')
  } else if (planetStore.planetStatsTab === 'ore_storage') {
    return t('planets.stats.titles.oreStorage')
  } else if (planetStore.planetStatsTab === 'scanning_range') {
    return t('planets.stats.titles.scanningRange')
  } else if (planetStore.planetStatsTab === 'defense_attack_ships') {
    return t('planets.stats.titles.activeDefense')
  } else if (planetStore.planetStatsTab === 'shields') {
    return t('planets.stats.titles.passiveDefense')
  } else if (planetStore.planetStatsTab === 'primary_ore') {
    return assetStore.findOreById(planetData.value.primaryOreId).oreName
  } else if (planetStore.planetStatsTab === 'secondary_ore') {
    return assetStore.findOreById(planetData.value.secondaryOreId).oreName
  }
  return t('planets.stats.titles.unknown')
})

const description = computed(() => {
  if(planetStore.planetStatsTab === 'food') {
    return t('planets.stats.descriptions.food')
  } else if (planetStore.planetStatsTab === 'production') {
    return t('planets.stats.descriptions.production')
  } else if (planetStore.planetStatsTab === 'science') {
    return t('planets.stats.descriptions.science')
  } else if (planetStore.planetStatsTab === 'credit') {
    return t('planets.stats.descriptions.credit')
  } else if (planetStore.planetStatsTab === 'energy') {
    return t('planets.stats.descriptions.energy')
  } else if (planetStore.planetStatsTab === 'energy_storage') {
    return t('planets.stats.descriptions.energyStorage')
  } else if (planetStore.planetStatsTab === 'ore_storage') {
    return t('planets.stats.descriptions.oreStorage')
  } else if (planetStore.planetStatsTab === 'scanning_range') {
    return t('planets.stats.descriptions.scanningRange')
  } else if (planetStore.planetStatsTab === 'defense_attack_ships') {
    return t('planets.stats.descriptions.activeDefense')
  } else if (planetStore.planetStatsTab === 'shields') {
    return t('planets.stats.descriptions.passiveDefense')
  } else if (planetStore.planetStatsTab === 'primary_ore') {
    return t('planets.stats.descriptions.primaryOre')
  } else if (planetStore.planetStatsTab === 'secondary_ore') {
    return t('planets.stats.descriptions.secondaryOre')
  }
  return t('planets.stats.descriptions.unknown')
})

const total = computed(() => {
  if(planetStore.planetStatsTab === 'food') {
    return stats.value.data.population_growth_per_turn
  } else if (planetStore.planetStatsTab === 'production') {
    return stats.value.data.production_per_turn
  } else if (planetStore.planetStatsTab === 'science') {
    return stats.value.data.science_per_turn
  } else if (planetStore.planetStatsTab === 'credit') {
    return stats.value.data.credits_per_turn
  } else if (planetStore.planetStatsTab === 'energy') {
    return stats.value.data.energy_per_turn
  } else if (planetStore.planetStatsTab === 'energy_storage') {
    return stats.value.data.energy_storage_capacity
  } else if (planetStore.planetStatsTab === 'ore_storage') {
    return stats.value.data.ore_storage_capacity
  } else if (planetStore.planetStatsTab === 'scanning_range') {
    return stats.value.data.scan_range
  } else if (planetStore.planetStatsTab === 'defense_attack_ships') {
    return stats.value.data.attack_ships
  } else if (planetStore.planetStatsTab === 'shields') {
    return stats.value.data.shields
  } else if (planetStore.planetStatsTab === 'primary_ore') {
    return stats.value.data.ore_per_turn
  } else if (planetStore.planetStatsTab === 'secondary_ore') {
    return stats.value.data.ore_per_turn
  }
  return 0
})

const hasDoakMinister = computed(() => gameStore.playerMinisters.has('production_converted_to_food'))
const isDoakActive = computed(() => {
  const minister = gameStore.playerMinisters.get('production_converted_to_food')
  return minister && minister.fired !== 1
})

const shouldShowDoakMessage = computed(() => {
  return planetStore.planetStatsTab === 'food' && 
         planetData.value.productionObjectId === 0 && 
         hasDoakMinister.value && 
         isDoakActive.value
})

/**
 * Functions
 */
const loadData = function (): void {
  loading.value = true;
  const backend = new Backend();

  backend.loadPlanetStats(props.planetId, planetStore.planetStatsTab)
    .then((response: { data: Record<string, any> }) => { // Adjust type for `data` as per actual structure
      loading.value = false;
      stats.value = response.data;
      logger.debug('Loaded planet stats:', stats.value);
    })
    .catch((error: unknown) => {
      loading.value = false;
      logger.error('Failed to load planet stats:', error);
    });
};

/**
 * Watchers
 */
watch(() => planetStore.planetStatsTab, () => {
  loadData()
})

watch(() => props.planetId, () => {
  loadData()
})

watch(() => [planetData.value.populationFoodPercentage,planetData.value.populationProductionPercentage,planetData.value.populationSciencePercentage,planetData.value.taxPercentage], () => {
  loadData()
})

/**
 * Lifecycle
 */
onMounted(() => {
  loadData()
})

</script>

<template>

  <div class="flex-grow">
    <StandardCard :title="title" topPadding="0">
    <div>
      <div class="text-sm text-subtext"> {{ description }}</div>
    </div>
    <div v-if="loading" class="flex justify-center">
      <Loader></Loader>
    </div>
    <div v-else class="">
      <div class="grid grid-cols-[1fr_auto] text-subtext mt-2 rounded p-1 sunken">
        <!--- Food Specific --->
        <ContentItem v-if="stats.data.from_workforce_farmers" :amount="stats.data.from_workforce_farmers"  :text="t('planets.stats.items.farmers')"/>
        <ContentItem v-if="stats.data.from_population_maintenance" :amount="stats.data.from_population_maintenance"  :text="t('planets.stats.items.populationUpkeep')"/>
        <!--- Production Specific --->
        <ContentItem v-if="stats.data.from_workforce_workers" :amount="stats.data.from_workforce_workers"  :text="t('planets.stats.items.workers')"/>
        <!--- Science Specific --->
        <ContentItem v-if="stats.data.from_workforce_scientists" :amount="stats.data.from_workforce_scientists"  :text="t('planets.stats.items.scientists')"/>

        <!--- From Planet--->
        <ContentItem v-if="stats.data.from_planet" :amount="stats.data.from_planet"  :text="t('planets.stats.items.base')"/>

        <!--- Buildings --->
        <template v-if="stats.data.from_buildings.length>0">
          <template v-for="building in stats.data.from_buildings" :key="building.id">
            <ContentItem :amount="building.amount"
                             :text="t(`buildings.buildings.${building.building_code}.name`)"
            ></ContentItem>
          </template>
        </template>

        <!--- Tax --->
        <ContentItem v-if="stats.data.from_tax" :amount="stats.data.from_tax"  :text="t('planets.stats.items.tax')"/>

        <!--- Type Bonus/Penalty --->
        <ContentItem v-if="stats.data.type_bonus" :amount="stats.data.type_bonus"  :text="t('planets.stats.items.bestPlanetType')"/>
        <ContentItem v-if="stats.data.type_penalty" :amount="stats.data.type_penalty"  :text="t('planets.stats.items.worstPlanetType')"/>

        <!--- Gravity Bonus/Penalty --->
        <ContentItem v-if="stats.data.gravity_bonus" :amount="stats.data.gravity_bonus"  :text="t('planets.stats.items.bestGravity')"/>
        <ContentItem v-if="stats.data.high_gravity_penalty" :amount="stats.data.high_gravity_penalty"  :text="t('planets.stats.items.gravityTooHigh')"/>
        <ContentItem v-if="stats.data.low_gravity_penalty" :amount="stats.data.low_gravity_penalty"  :text="t('planets.stats.items.gravityTooLow')"/>

        <!--- Toxic Ore Penalty --->
        <ContentItem v-if="stats.data.toxic" :amount="stats.data.toxic"  :text="t('planets.stats.items.oreToxicity')"/>

        <!--- Ferion Planet Bonus --->
        <ContentItem v-if="stats.data.ferion_bonus" :amount="stats.data.ferion_bonus"  :text="t('planets.stats.items.ferionBonus')"/>

        <!--- Ringworld Bonus --->
        <ContentItem v-if="stats.data.ringworld_bonus" :amount="stats.data.ringworld_bonus"  :text="t('planets.stats.items.ringworldBonus')"/>

        <!--- Total --->
      </div>
      <div class="grid grid-cols-[1fr_auto] text-subtext mt-2 rounded p-1 sunken">
        <ContentItem :amount="total"  :text="t('planets.stats.items.total')"/>
      </div>

      <div class="text-subtext text-xs pt-1 ">{{ t('planets.stats.notes.rounded') }}</div>
      <div v-if="shouldShowDoakMessage" class="text-subtext text-xs pt-1">
        {{ t('planets.stats.notes.doakMinister') }}
      </div>
    </div>
    </StandardCard>
  </div>
</template>

<style scoped lang="scss">
.content {
  @apply bg-metal-800 mb-1 flex-grow rounded-e;
}

.sunken {
  background: rgba(15, 18, 22, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.3);
}
</style>